import React, { useCallback, useEffect, useState } from 'react'
import { App, Button, Space, Table } from 'antd'
import { brand } from '../../theme/brand'
import { ListPage, Pill, TableSurface, useRowShow, type Stat, type Tone } from '../../theme/listkit'
import { supabaseClient } from '../../lib/supabaseClient'
import { groupsAdminErrorMessage } from '../groups/adminErrors'
import { categoryLabel } from '../groups/categoryOptions'
import { Sparkles, UsersRound, RefreshCw, BadgeCheck } from '../../components/icons'

export interface IdeaRow {
  id: string
  title: string
  tagline: string | null
  category: string
  place_label: string | null
  is_virtual: boolean
  source: string
  suggested_by: string | null
  organizer_user_id: string | null
  status: string
  pledge_count: number
  created_at: string
  organizer_name?: string | null
}

export const ideaStatusTone = (status: string): Tone => {
  if (status === 'open') return 'gold'
  if (status === 'converted') return 'green'
  return 'neutral' // closed
}

export const sourceLabel = (source: string): string => {
  if (source === 'member') return 'Member'
  if (source === 'association') return 'Association'
  if (source === 'city') return 'City'
  return source
}

/**
 * Group ideas — the pledge board's admin side (§P "Groups we're starting").
 * Direct supabaseClient reads: organizer_user_id/suggested_by reference
 * auth.users (no FK to profiles), same reasoning as GroupMembersTable and
 * the Proposals queue.
 */
export function GroupIdeasList() {
  const { message } = App.useApp()
  const [rows, setRows] = useState<IdeaRow[]>([])
  const [loading, setLoading] = useState(true)
  const [generating, setGenerating] = useState(false)
  const onRow = useRowShow('group_ideas')

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const { data: ideas, error } = await supabaseClient
        .from('group_ideas')
        .select(
          'id, title, tagline, category, place_label, is_virtual, source, suggested_by, organizer_user_id, status, pledge_count, created_at',
        )
        .order('created_at', { ascending: false })
      if (error) throw error

      const ids = (ideas ?? []).map((i) => i.organizer_user_id).filter((id): id is string => Boolean(id))
      const byId = new Map<string, string>()
      if (ids.length > 0) {
        const { data: profiles } = await supabaseClient.from('profiles').select('user_id, name').in('user_id', ids)
        for (const p of profiles ?? []) {
          if (p.user_id) byId.set(p.user_id, p.name ?? 'Unknown member')
        }
      }
      setRows(
        (ideas ?? []).map((i) => {
          const oid = i.organizer_user_id
          return { ...i, organizer_name: oid ? byId.get(oid) : null }
        }),
      )
    } catch (e) {
      message.error(groupsAdminErrorMessage(e))
    } finally {
      setLoading(false)
    }
  }, [message])

  useEffect(() => {
    void load()
  }, [load])

  async function generateCityIdeas() {
    setGenerating(true)
    try {
      const { data, error } = await supabaseClient.rpc('generate_city_ideas')
      if (error) throw error
      message.success(`${data ?? 0} new city idea${data === 1 ? '' : 's'} generated.`)
      void load()
    } catch (e) {
      message.error(groupsAdminErrorMessage(e))
    } finally {
      setGenerating(false)
    }
  }

  const open = rows.filter((r) => r.status === 'open').length
  const converted = rows.filter((r) => r.status === 'converted').length
  const ready = rows.filter((r) => r.status === 'open' && r.organizer_user_id && r.pledge_count >= 8).length

  const stats: Stat[] = [
    { label: 'Open', value: open.toLocaleString(), icon: Sparkles, tone: open > 0 ? 'gold' : 'neutral' },
    { label: 'Ready to convert', value: ready.toLocaleString(), icon: UsersRound, tone: ready > 0 ? 'green' : 'neutral' },
    { label: 'Converted', value: converted.toLocaleString(), icon: BadgeCheck, tone: converted > 0 ? 'green' : 'neutral' },
  ]

  return (
    <ListPage
      intro={{
        eyebrow: 'Community · Groups',
        description:
          'The pledge board — "Groups we\'re starting". Members suggest a group before it exists; when it has an organizer and reaches the pledge threshold, convert it into a real group here.',
        actions: (
          <Button
            data-testid="generate-city-ideas"
            icon={<RefreshCw size={14} />}
            loading={generating}
            onClick={() => void generateCityIdeas()}
          >
            Generate city ideas
          </Button>
        ),
      }}
      stats={stats}
      statColumns={3}
    >
      <TableSurface>
        <Table rowKey="id" loading={loading} dataSource={rows} scroll={{ x: true }} onRow={onRow} pagination={{ pageSize: 20 }}>
          <Table.Column<IdeaRow>
            dataIndex="title"
            title="Idea"
            render={(value: string, record) => (
              <span>
                <span style={{ fontFamily: brand.fSans, fontWeight: 700, color: brand.ink }}>{value}</span>
                {record.tagline && (
                  <div style={{ fontFamily: brand.fSans, fontWeight: 500, fontSize: 11.5, color: brand.sub }}>
                    {record.tagline}
                  </div>
                )}
              </span>
            )}
          />
          <Table.Column<IdeaRow> dataIndex="category" title="Category" render={(v: string) => <Pill tone="jaguar">{categoryLabel(v)}</Pill>} />
          <Table.Column<IdeaRow> dataIndex="source" title="Source" render={(v: string) => <Pill tone="neutral">{sourceLabel(v)}</Pill>} />
          <Table.Column<IdeaRow>
            dataIndex="pledge_count"
            title="Pledges"
            sorter={(a, b) => a.pledge_count - b.pledge_count}
            render={(v: number) => (
              <Space size={4}>
                <UsersRound size={13} color={brand.sub} />
                <span style={{ fontFamily: brand.fSans, fontWeight: 600, color: brand.ink }}>{v} / 8</span>
              </Space>
            )}
          />
          <Table.Column<IdeaRow>
            dataIndex="organizer_name"
            title="Organizer"
            render={(v: string | null) => (v ? v : <span style={{ color: brand.sub, fontStyle: 'italic' }}>None yet</span>)}
          />
          <Table.Column<IdeaRow> dataIndex="status" title="Status" render={(v: string) => <Pill tone={ideaStatusTone(v)}>{v}</Pill>} />
        </Table>
      </TableSurface>
    </ListPage>
  )
}
